// src/modules/Admin/HistoryReceipts/HistoryPage.jsx

import React, { useState, useMemo, useEffect } from "react";
import api from "../../../services/api";
import PageLayout from "../../../components/layout/PageLayout";
import { Header, DateRangePicker } from "../../../components/ui";
import {
  FaHistory,
  FaMapMarkerAlt,
  FaBox,
  FaRubleSign,
  FaExchangeAlt,
} from "react-icons/fa";
import styles from "./HistoryPage.module.css";

export default function HistoryPage() {
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [receipts, setReceipts] = useState([]);        // ← данные от бэка { receipt, history: [] }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState("table");
  const [actionFilter, setActionFilter] = useState("all"); // all | moved

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  // ===== Загрузка данных =====
  const fetchHistory = async (range = dateRange) => {
    if (!range?.from || !range?.to) return;
    setLoading(true);
    setError(null);

    try {
      const res = await api.get("/receipts/history/by-date", {
        params: { startDate: range.from, endDate: range.to },
      });

      // Бэкенд: return res.json({ receipts: result });
      setReceipts(res.receipts || []);
      setCurrentPage(1);
    } catch (e) {
      setError(e.response?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dateRange.from && dateRange.to) fetchHistory(dateRange);
  }, [dateRange]);

  // ===== Хелперы форматирования =====
  const formatMoney = (value) => {
    if (value == null) return "";
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    return num.toLocaleString("ru-RU", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatDuration = (ms) => {
    if (!ms || ms <= 0) return "0 мин";
    const totalMinutes = Math.floor(ms / 60000);
    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const mins = totalMinutes % 60;

    const parts = [];
    if (days) parts.push(`${days} д.`);
    if (hours) parts.push(`${hours} ч.`);
    if (mins || parts.length === 0) parts.push(`${mins} мин`);
    return parts.join(" ");
  };

  // статус: класс для бейджа
  const getStatusClass = (statusRaw) => {
    if (!statusRaw) return "";
    const status = String(statusRaw).toLowerCase();

    if (status === "open" || status === "открыта") return styles.status_open;
    if (status === "closed" || status === "закрыта") return styles.status_closed;
    if (status === "failed" || status === "не доставлено") return styles.status_failed;
    return "";
  };

  // статус: текст для отображения
  const getStatusText = (statusRaw) => {
    if (!statusRaw) return "—";
    const status = String(statusRaw).toLowerCase();

    if (status === "open") return "Открыта";
    if (status === "closed") return "Закрыта";
    if (status === "failed") return "Не доставлено";

    // если уже пришёл на русском — просто отдадим как есть
    return statusRaw;
  };

  // ===== Подготовка данных: квитанция + история в единый формат =====
  const rows = useMemo(() => {
    return (receipts || []).map((item) => {
      const r = item.receipt;
      const hArr = item.history || [];

      const routes = hArr.map((h) => ({
        id: h.route_id,
        number: h.route_number,
        date: h.route_date,
        action: h.action_type,
        from: h.from_route_id,
        driver: h.route?.driver || null,
        changed_at: h.changed_at,
      }));

      // отсортируем маршруты по времени
      routes.sort(
        (a, b) => new Date(a.changed_at).getTime() - new Date(b.changed_at).getTime()
      );

      return {
        receipt: r,
        routes,
      };
    });
  }, [receipts]);

  // ===== Метрики по квитанции =====
  const getMoveCount = (row) =>
    row.routes.filter((r) => r.action === "moved").length;

  const getDurationForRow = (row) => {
    if (!row.routes.length) return "—";
    const firstAdded = row.routes.find((r) => r.action === "added") || row.routes[0];
    const lastEvent = row.routes[row.routes.length - 1];
    const start = new Date(firstAdded.changed_at).getTime();
    const end = new Date(lastEvent.changed_at).getTime();
    if (!start || !end) return "—";
    return formatDuration(end - start);
  };

  // бейдж количества переносов
  const getMovesBadgeClass = (moves) => {
    if (moves === 0) return styles.movesZero;
    if (moves === 1) return styles.movesLow;
    if (moves === 2) return styles.movesMedium;
    return styles.movesHigh;
  };

  // цвет кружка маршрута
  const getRouteClass = (index) => {
    if (index === 0) return styles.timelineFirst;   // первый маршрут — зелёный
    if (index === 1) return styles.timelineSecond;  // второй — жёлтый
    return styles.timelineOther;                    // остальные — красные
  };

  // ===== Фильтр по типу действий =====
  const filtered = useMemo(() => {
    if (actionFilter === "moved") {
      return rows.filter((r) => r.routes.some((x) => x.action === "moved"));
    }
    return rows;
  }, [rows, actionFilter]);

  const totalPages = Math.ceil(filtered.length / pageSize) || 1;
  const paginated = filtered.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // ===== Таймлайн маршрутов справа =====
  const renderTimeline = (routes) => {
    if (!routes || routes.length === 0) {
      return <div className={styles.noHistory}>История маршрутов отсутствует</div>;
    }

    return (
      <div className={styles.timeline}>
        {routes.map((r, i) => (
          <div key={i} className={styles.timelineItem}>
            <div className={`${styles.timelineDot} ${getRouteClass(i)}`} />
            <div className={styles.timelineContent}>
              <div className={styles.timelineHeader}>
                <div className={styles.timelineRoute}>Маршрут {r.number}</div>
                <div className={styles.timelineTime}>
                  {new Date(r.changed_at).toLocaleDateString("ru-RU")}{" "}
                  {new Date(r.changed_at).toLocaleTimeString("ru-RU", {
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })}
                </div>
              </div>
              <div className={styles.timelineAction}>
                {r.action === "added" ? "Добавлена в маршрут" : "Перемещена"}
                {r.from ? ` из ${r.from}` : ""}
              </div>
              {r.driver && (
                <div className={styles.timelineMeta}>Водитель: {r.driver}</div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // ===== Продукты: ограничиваем список, чтобы не раздувать карточку =====
  const renderProducts = (receipt) => {
    const products = receipt.products || [];
    if (!products.length) return null;

    const maxVisible = 3;
    const visible = products.slice(0, maxVisible);
    const hiddenCount = products.length - visible.length;

    return (
      <div className={styles.products}>
        {visible.map((p, i) => (
          <div key={i} className={styles.productItem}>
            {p.product_name} × {p.actual_quantity}
          </div>
        ))}
        {hiddenCount > 0 && (
          <div className={styles.productsMore}>+ ещё {hiddenCount}</div>
        )}
      </div>
    );
  };

  // ===== TABLE VIEW =====
  const renderTableView = () => (
    <>
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Квитанция</th>
              <th>Клиент / Адрес</th>
              <th>Сумма</th>
              <th>Статус</th>
              <th>Переносов / Время</th>
              <th>История маршрутов</th>
            </tr>
          </thead>
          <tbody>
            {paginated.map((row, idx) => {
              const r = row.receipt;
              const moves = getMoveCount(row);
              const duration = getDurationForRow(row);

              return (
                <tr key={idx}>
                  <td>#{r.receipt_number}</td>
                  <td>
                    <div>{r.client_name}</div>
                    <div className={styles.addr}>{r.delivery_address}</div>
                  </td>
                  <td>{formatMoney(r.amount)}</td>
                  <td>
                    <span
                      className={`${styles.statusBadge} ${getStatusClass(
                        r.status
                      )}`}
                    >
                      {getStatusText(r.status)}
                    </span>
                  </td>
                  <td>
                    <div
                      className={`${styles.movesBadge} ${getMovesBadgeClass(
                        moves
                      )}`}
                    >
                      Переносов: {moves}
                    </div>
                    <div className={styles.duration}>Путь: {duration}</div>
                  </td>
                  <td>{renderTimeline(row.routes)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {filtered.length > pageSize && (
        <div className={styles.pagination}>
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            Пред.
          </button>
          <span>
            Стр. {currentPage} из {totalPages}
          </span>
          <button
            onClick={() =>
              setCurrentPage((p) => Math.min(totalPages, p + 1))
            }
            disabled={currentPage === totalPages}
          >
            След.
          </button>
        </div>
      )}
    </>
  );

  // ===== CARD VIEW =====
  const renderCardsView = () => (
    <>
      <div className={styles.cardsWrapper}>
        {paginated.map((row, idx) => {
          const r = row.receipt;
          const moves = getMoveCount(row);
          const duration = getDurationForRow(row);

          return (
            <div key={idx} className={styles.card}>
              <div className={styles.cardHeader}>
                <a className={styles.cardNumber}>#{r.receipt_number}</a>
                <div
                  className={`${styles.movesBadge} ${getMovesBadgeClass(
                    moves
                  )}`}
                >
                  Переносов: {moves}
                </div>
              </div>

              <div className={styles.cardSubHeader}>
                <span
                  className={`${styles.statusBadge} ${getStatusClass(
                    r.status
                  )}`}
                >
                  {getStatusText(r.status)}
                </span>
                <span className={styles.durationInline}>Путь: {duration}</span>
              </div>

              <div className={styles.cardGrid}>
                {/* Левая колонка: квитанция + товары */}
                <div className={styles.cardColLeft}>
                  <div className={styles.row}>
                    <FaMapMarkerAlt />
                    <span>{r.delivery_address}</span>
                  </div>
                  <div className={styles.row}>
                    <FaBox />
                    <span>{r.client_name}</span>
                  </div>
                  <div className={styles.row}>
                    <FaRubleSign />
                    <span>Сумма: {formatMoney(r.amount)}</span>
                  </div>
                  <div className={styles.row}>
                    <FaExchangeAlt />
                    <span>Район: {r.district}</span>
                  </div>

                  {renderProducts(r)}
                </div>

                {/* Правая колонка: история маршрутов */}
                <div className={styles.cardColRight}>
                  <div className={styles.historyTitle}>История маршрутов:</div>
                  {renderTimeline(row.routes)}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filtered.length > pageSize && (
        <div className={styles.pagination}>
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            Пред.
          </button>
          <span>
            Стр. {currentPage} из {totalPages}
          </span>
          <button
            onClick={() =>
              setCurrentPage((p) => Math.min(totalPages, p + 1))
            }
            disabled={currentPage === totalPages}
          >
            След.
          </button>
        </div>
      )}
    </>
  );

  // ===== Рендер =====
  return (
    <PageLayout
      header={
        <Header
          icon={FaHistory}
          title="История перемещений"
          subtitle="Все маршруты, где была квитанция"
        />
      }
      controls={
        <>
          <DateRangePicker
            onChange={(v) => v?.from && v?.to && setDateRange(v)}
          />

          <div className={styles.filterRow}>
            <span>Показать:</span>
            <select
              className={styles.select}
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
            >
              <option value="all">Все</option>
              <option value="moved">Только перемещённые</option>
            </select>

            <div className={styles.viewSwitch}>
              <button
                className={`${styles.switchBtn} ${
                  viewMode === "table" ? styles.active : ""
                }`}
                onClick={() => setViewMode("table")}
              >
                Таблица
              </button>
              <button
                className={`${styles.switchBtn} ${
                  viewMode === "cards" ? styles.active : ""
                }`}
                onClick={() => setViewMode("cards")}
              >
                Карточки
              </button>
            </div>
          </div>
        </>
      }
    >
      {loading && <p className={styles.loading}>Загрузка...</p>}
      {error && <p className={styles.error}>{error}</p>}
      {!loading && filtered.length === 0 && (
        <p className={styles.noData}>Нет данных</p>
      )}
      {!loading && filtered.length > 0 && (
        viewMode === "table" ? renderTableView() : renderCardsView()
      )}
    </PageLayout>
  );
}
